import React, { useEffect, useMemo, useState } from 'react'
import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../../common/SafeIcon'
import {
  createCourse,
  deleteCourse,
  fetchCoursesForCurriculum,
  reorderCourses,
  updateCourse
} from '../../../config/courseService'
import { supabase, supabaseConfigError } from '../../../config/supabase'

const {
  FiPlus,
  FiTrash2,
  FiRefreshCw,
  FiEdit2,
  FiCheck,
  FiX,
  FiGripVertical,
  FiBook,
  FiAlertTriangle,
  FiList
} = FiIcons

const emptyCourse = {
  id: null,
  name: '',
  description: '',
  estimated_duration: '',
  order_index: 0
}

const SortableCourseCard = ({ course, onEdit, onDelete, onManageLessons, lessonCount, dragHandleProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: course.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm flex items-start gap-3"
    >
      <button
        className="p-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 focus:outline-none"
        {...attributes}
        {...listeners}
        {...dragHandleProps}
      >
        <SafeIcon icon={FiGripVertical} className="w-4 h-4" />
      </button>
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiBook} className="w-4 h-4 text-[#00AAFF]" />
          <p className="font-semibold text-gray-900">{course.name}</p>
        </div>
        {course.description && (
          <p className="text-sm text-gray-700 leading-relaxed">{course.description}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {course.estimated_duration && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-700">
              Est. duration: {course.estimated_duration}
            </span>
          )}
          {typeof lessonCount === 'number' && (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-700">
              {lessonCount} lesson{lessonCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {onManageLessons && (
          <button
            className="p-2 rounded-lg hover:bg-[#00AAFF]/10 text-[#00AAFF]"
            onClick={() => onManageLessons(course)}
            title="Manage lessons"
          >
            <SafeIcon icon={FiList} className="w-4 h-4" />
          </button>
        )}
        <button
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
          onClick={() => onEdit(course)}
        >
          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
        </button>
        <button
          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
          onClick={() => onDelete(course)}
        >
          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

const CourseManager = ({ curriculum, onManageLessons, curriculumId: curriculumIdProp }) => {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [formValues, setFormValues] = useState(emptyCourse)
  const [editingId, setEditingId] = useState(null)
  const [lessonCountByCourse, setLessonCountByCourse] = useState({})

  const curriculumId = curriculumIdProp ?? curriculum?.id

  const canEdit = useMemo(() => Boolean(curriculumId), [curriculumId])

  useEffect(() => {
    setCourses([])
    setFormValues(emptyCourse)
    setEditingId(null)
    setError(null)
    if (curriculumId) {
      loadCourses(curriculumId)
    }
  }, [curriculumId])

  const loadCourses = async (curriculumIdToLoad) => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }
    try {
      setLoading(true)
      const { data, error: fetchError } = await fetchCoursesForCurriculum(curriculumIdToLoad)
      if (fetchError) throw fetchError
      setCourses(data || [])
      loadLessonCounts(curriculumIdToLoad)
    } catch (fetchErr) {
      console.error('Error fetching courses', fetchErr)
      setError('Unable to load courses right now.')
    } finally {
      setLoading(false)
    }
  }

  const loadLessonCounts = async (curriculumIdToLoad) => {
    if (supabaseConfigError || !curriculumIdToLoad) return
    try {
      const { data: lessonRows } = await supabase
        .from('lessons')
        .select('course_id')
        .eq('curriculum_id', curriculumIdToLoad)
      const counts = {}
      ;(lessonRows || []).forEach((row) => {
        if (row.course_id) counts[row.course_id] = (counts[row.course_id] || 0) + 1
      })
      setLessonCountByCourse(counts)
    } catch {
      setLessonCountByCourse({})
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!canEdit) return
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }

    const payload = {
      name: formValues.name,
      description: formValues.description,
      estimated_duration: formValues.estimated_duration
    }

    try {
      setSaving(true)
      setError(null)
      if (editingId) {
        const { data, error: updateError } = await updateCourse(editingId, {
          ...payload,
          curriculum_id: curriculumId,
          order_index: formValues.order_index ?? 0
        })
        if (updateError) throw updateError
        setCourses((existing) =>
          existing.map((course) => (course.id === editingId ? data : course))
        )
      } else {
        const { data, error: createError } = await createCourse(
          curriculumId,
          payload,
          courses.length
        )
        if (createError) throw createError
        setCourses((existing) => [...existing, data])
      }
      setFormValues(emptyCourse)
      setEditingId(null)
    } catch (submitError) {
      console.error('Error saving course', submitError)
      setError('Unable to save this course. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (course) => {
    setFormValues(course)
    setEditingId(course.id)
  }

  const handleDelete = async (course) => {
    if (!course?.id) return
    const previousCourses = courses
    setCourses((existing) => existing.filter((item) => item.id !== course.id))
    try {
      const { error: deleteError } = await deleteCourse(course.id)
      if (deleteError) throw deleteError
    } catch (deleteErr) {
      console.error('Error deleting course', deleteErr)
      setCourses(previousCourses)
      setError('Unable to delete this course right now.')
    }
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = courses.findIndex((course) => course.id === active.id)
    const newIndex = courses.findIndex((course) => course.id === over.id)

    const reordered = arrayMove(courses, oldIndex, newIndex).map((course, index) => ({
      ...course,
      order_index: index
    }))

    setCourses(reordered)
    const orderedIds = reordered.map((course) => course.id)
    try {
      setSaving(true)
      setError(null)
      const { error: reorderError } = await reorderCourses(curriculumId, orderedIds)
      if (reorderError) throw reorderError
    } catch (reorderErr) {
      console.error('Error reordering courses', reorderErr)
      setError('We could not save the new order. Please try again.')
      // Reload to maintain server truth
      loadCourses(curriculumId)
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setFormValues(emptyCourse)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-700 font-semibold">
          <SafeIcon icon={FiBook} className="w-5 h-5 text-[#00AAFF]" />
          Courses in this curriculum
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : `${courses.length} item${courses.length === 1 ? '' : 's'}`}
        </div>
      </div>

      {!canEdit ? (
        <div className="p-6 text-center text-gray-600">
          <p className="font-semibold mb-1">Select a curriculum to manage courses</p>
          <p className="text-sm text-gray-500">Choose a curriculum from the list to get started.</p>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700">
              <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5" />
              <div>
                <p className="font-semibold">We ran into an issue</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 bg-gray-50 p-4 rounded-xl">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-gray-800">
                {editingId ? 'Edit course' : 'Add a course'}
              </p>
              {saving && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                  <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin" />
                  Saving...
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-gray-500">Name</label>
                <input
                  type="text"
                  value={formValues.name}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                  placeholder="e.g. Algebra Foundations"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-wide text-gray-500">
                  Estimated duration
                </label>
                <input
                  type="text"
                  value={formValues.estimated_duration}
                  onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, estimated_duration: e.target.value }))
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                  placeholder="e.g. 6 weeks"
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs uppercase tracking-wide text-gray-500">
                  Description
                </label>
                <textarea
                  value={formValues.description}
                  onChange={(e) =>
                    setFormValues((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                  placeholder="What will learners accomplish?"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-200"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5]"
                disabled={saving}
              >
                <SafeIcon icon={editingId ? FiCheck : FiPlus} className="w-4 h-4" />
                {editingId ? 'Save changes' : 'Add course'}
              </button>
            </div>
          </form>

          <div className="space-y-3">
            {loading ? (
              [1, 2, 3].map((skeleton) => (
                <div key={skeleton} className="h-16 rounded-xl bg-gray-100 animate-pulse" />
              ))
            ) : courses.length === 0 ? (
              <div className="p-4 text-center text-gray-600 border border-dashed border-gray-200 rounded-xl">
                <p className="font-semibold mb-1">No courses yet</p>
                <p className="text-sm text-gray-500">
                  Add your first course to start building this curriculum.
                </p>
              </div>
            ) : (
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={courses.map((course) => course.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {courses.map((course) => (
                      <SortableCourseCard
                        key={course.id}
                        course={course}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onManageLessons={onManageLessons}
                        lessonCount={lessonCountByCourse[course.id]}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CourseManager
